import React, { useState } from 'react';
import type { PipelineState, StageStatus } from './types';
import { StatusBadge } from './common';

interface PipelineViewProps {
    title: PipelineState;
    onBack: () => void;
    onRepairStep: (step: 'text' | 'audio' | 'art' | 'manifest', sourceUrl?: string) => void;
    onRepairAll: () => void;
    onRebuildThumbs?: (id: string) => Promise<any>;
    onGetJobStatus?: (id: string) => Promise<any>;
}

const StageCard = ({ label, status, details, onRepair, onRebuildThumbs, isTextStage, isArtStage }: {
    label: string,
    status: StageStatus,
    details?: string,
    onRepair: (url?: string) => void,
    onRebuildThumbs?: () => void,
    isTextStage?: boolean,
    isArtStage?: boolean
}) => {
    // ... (Keep existing StageCard implementation) ...
    const isOk = status === 'ok';
    const isRunning = status === 'running';
    const [manualUrl, setManualUrl] = useState('');
    const [showInput, setShowInput] = useState(false);

    const handleRepair = () => {
        if (isTextStage && showInput && manualUrl) {
            onRepair(manualUrl);
        } else {
            onRepair();
        }
    };

    return (
        <div style={{
            flex: 1,
            background: '#252525',
            border: `1px solid ${status === 'broken' ? '#ef5350' : status === 'running' ? '#2196f3' : '#444'}`,
            borderRadius: '8px',
            padding: '15px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            opacity: status === 'pending' ? 0.6 : 1
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ margin: 0, color: '#90caf9' }}>{label}</h4>
                <StatusBadge status={status} />
            </div>

            <div style={{ fontSize: '12px', color: '#ccc', minHeight: '40px' }}>
                {details || 'No details available.'}
            </div>

            {/* ACTION AREA */}
            <div style={{ marginTop: 'auto', paddingTop: '10px', borderTop: '1px solid #333' }}>
                {isOk ? (
                    <div style={{ fontSize: '11px', color: '#4caf50', textAlign: 'center' }}>✓ Verified</div>
                ) : isRunning ? (
                    <div style={{ fontSize: '11px', color: '#2196f3', textAlign: 'center' }}>⏳ Processing...</div>
                ) : (
                    <>
                        {isTextStage && !isOk && (
                            <div style={{ marginBottom: 8 }}>
                                {showInput ? (
                                    <input
                                        type="text"
                                        placeholder="Paste Gutenberg/Source URL"
                                        value={manualUrl}
                                        onChange={e => setManualUrl(e.target.value)}
                                        style={{ width: '100%', padding: 5, fontSize: 11, marginBottom: 5, background: '#333', color: 'white', border: '1px solid #555' }}
                                    />
                                ) : (
                                    <div style={{ fontSize: 10, color: '#888', textAlign: 'center', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setShowInput(true)}>
                                        Manual Source URL?
                                    </div>
                                )}
                            </div>
                        )}
                        <button
                            onClick={handleRepair}
                            style={{
                                width: '100%',
                                padding: '8px',
                                background: '#e65100',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                fontSize: '11px'
                            }}>
                            {isTextStage && showInput ? 'SAVE & RETRY' : 'REPAIR / GENERATE'}
                        </button>

                        {isArtStage && onRebuildThumbs && (
                            <button
                                onClick={onRebuildThumbs}
                                style={{
                                    width: '100%',
                                    marginTop: '8px',
                                    padding: '8px',
                                    background: '#006064',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    fontSize: '11px'
                                }}>
                                REBUILD THUMBS
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export const PipelineDetailedView = ({ title, onBack, onRepairStep, onRepairAll, onRebuildThumbs, onGetJobStatus }: PipelineViewProps) => {
    const [liveStatus, setLiveStatus] = useState<any>(null);

    React.useEffect(() => {
        if (title.activeJob && onGetJobStatus) {
            onGetJobStatus(title.activeJob.id).then(r => setLiveStatus(r));
        }
    }, [title.activeJob, onGetJobStatus]);

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* HEADER */}
            <div style={{
                padding: '20px',
                background: '#1e1e1e',
                borderBottom: '1px solid #333',
                display: 'flex',
                alignItems: 'center',
                gap: '20px'
            }}>
                <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: '#aaa', fontSize: '18px', cursor: 'pointer' }}>←</button>
                <div>
                    <h2 style={{ margin: 0, fontSize: '18px', color: '#fff' }}>{title.title}</h2>
                    <div style={{ fontFamily: 'monospace', color: '#666', fontSize: '12px' }}>ID: {title.id}</div>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                    <StatusBadge status={title.overall} label={`Overall: ${title.overall}`} />
                </div>
            </div>

            {/* SCROLLABLE CONTENT */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
                {/* VISUAL PIPELINE */}
                <div style={{
                    padding: '40px',
                    display: 'flex',
                    gap: '20px',
                    alignItems: 'stretch',
                    overflowX: 'auto'
                }}>
                    <StageCard
                        label="1. TEXT"
                        status={title.stages.text.status}
                        details={title.stages.text.details}
                        onRepair={(url) => onRepairStep('text', url)}
                        isTextStage={true}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', color: '#444' }}>→</div>
                    <StageCard
                        label="2. AUDIO"
                        status={title.stages.audio.status}
                        details={title.stages.audio.details}
                        onRepair={() => onRepairStep('audio')} // No URL for audio
                    />
                    <div style={{ display: 'flex', alignItems: 'center', color: '#444' }}>→</div>
                    <StageCard
                        label="3. ARTWORK"
                        status={title.stages.art.status}
                        details={title.stages.art.details}
                        onRepair={() => onRepairStep('art')}
                        isArtStage={true}
                        onRebuildThumbs={onRebuildThumbs ? async () => {
                            if (confirm("Rebuild thumbnails?")) {
                                const res = await onRebuildThumbs(title.id);
                                if (res.ok) alert("Rebuilt: " + res.details);
                                else alert("Error: " + res.error);
                            }
                        } : undefined}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', color: '#444' }}>→</div>
                    <StageCard
                        label="4. MANIFEST"
                        status={title.stages.manifest.status}
                        details={title.stages.manifest.details}
                        onRepair={() => onRepairStep('manifest')}
                    />
                </div>

                {/* LIVE JOB STATUS (If Active) */}
                {liveStatus ? (
                    <div style={{ padding: '0 40px 40px 40px' }}>
                        <div style={{ background: '#111', border: '1px solid #333', borderRadius: '4px', padding: '15px' }}>
                            <h4 style={{ margin: '0 0 10px 0', color: '#81d4fa', borderBottom: '1px solid #333', paddingBottom: '5px' }}>
                                Active Job Detail
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', fontSize: '12px' }}>
                                <div>
                                    <div style={{ color: '#888' }}>JOB ID</div>
                                    <div style={{ fontFamily: 'monospace', marginBottom: '10px' }}>{liveStatus.job?.id}</div>
                                    <div style={{ color: '#888' }}>STEP</div>
                                    <div style={{ color: '#fff', marginBottom: '10px' }}>{liveStatus.job?.step || liveStatus.job?.processingState?.phase}</div>
                                    <div style={{ color: '#888' }}>LAST ERROR</div>
                                    <div style={{ color: '#ef5350' }}>{liveStatus.job?.lastError || 'None'}</div>
                                </div>
                                <div style={{ overflow: 'hidden' }}>
                                    <div style={{ color: '#888' }}>PARAMS</div>
                                    <pre style={{
                                        margin: 0, background: '#000', padding: '10px', borderRadius: '4px', border: '1px solid #333',
                                        maxHeight: '150px', overflow: 'auto', color: '#ccc'
                                    }}>
                                        {JSON.stringify(liveStatus.job?.params, null, 2)}
                                    </pre>
                                </div>
                            </div>
                            {liveStatus.files && (
                                <div style={{ marginTop: '10px', borderTop: '1px solid #333', paddingTop: '10px' }}>
                                    <div style={{ color: '#888', fontSize: '11px' }}>FILES DETECTED (Worker Check)</div>
                                    <div style={{ display: 'flex', gap: '10px', fontSize: '11px', marginTop: '5px' }}>
                                        {['cover', 'cleaned', 'job'].map(f => (
                                            <span key={f} style={{
                                                color: liveStatus.files?.[f] ? '#4caf50' : '#666',
                                                border: `1px solid ${liveStatus.files?.[f] ? '#4caf50' : '#444'}`,
                                                padding: '2px 5px', borderRadius: '2px'
                                            }}>
                                                {f}: {liveStatus.files?.[f] ? 'Yes' : 'No'}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : title.derivedStatus ? (
                    <div style={{ padding: '0 40px 40px 40px' }}>
                        <div style={{ background: '#1c1c1c', border: '1px solid #333', borderRadius: '4px', padding: '15px' }}>
                            <h4 style={{ margin: '0 0 10px 0', color: '#66bb6a', borderBottom: '1px solid #333', paddingBottom: '5px' }}>
                                Book Inspection
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', fontSize: '12px' }}>
                                <div>
                                    <div style={{ color: '#888' }}>STATUS</div>
                                    <div style={{ color: '#fff', marginBottom: '10px', fontWeight: 'bold' }}>{title.derivedStatus.status.toUpperCase()}</div>
                                    <div style={{ color: '#888' }}>REASON</div>
                                    <div style={{ color: '#ccc', marginBottom: '10px' }}>{title.derivedStatus.reason}</div>
                                    <div style={{ color: '#888' }}>LAST UPDATED</div>
                                    <div style={{ color: '#aaa' }}>{title.updatedAt || '—'}</div>
                                </div>
                                <div>
                                    <div style={{ color: '#888', marginBottom: '5px' }}>ASSETS (R2)</div>
                                    <div style={{ display: 'grid', gap: '5px', fontSize: '11px' }}>
                                        {Object.entries(title.health || {}).map(([k, v]) => (
                                            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #222' }}>
                                                <span style={{ color: '#888' }}>{k.replace('has', '')}</span>
                                                <span style={{ color: v ? '#4caf50' : '#444', fontWeight: 'bold' }}>{v ? 'YES' : '—'}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>

            {/* FOOTER ACTIONS */}
            <div style={{
                padding: '20px',
                background: '#222',
                borderTop: '1px solid #333',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <div style={{ fontSize: '12px', color: '#666' }}>
                    {title.activeJob ? `Job ${title.activeJob.id} active` : 'No active jobs'}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        onClick={onRepairAll}
                        style={{
                            background: '#1565c0',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                        }}>
                        RUN FULL REPAIR PIPELINE
                    </button>
                </div>
            </div>
        </div>
    );
};
